import { Vocabulary } from "../../Utils/Vocabulary";
import { Button, Grid, TextField, Box, Alert, IconButton, InputAdornment } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { urlEnum } from "../../Utils/urlEnum";
import { getData } from "../../Services/getData";
import { useEffect, useState, ChangeEvent, useContext } from "react";
import GenericModal from "../../Modal/GenericModal";
import { postData } from "../../Services/postData";
import { CacheContext } from "../../Context/cacheContext";
import { getActiveLocations, isMobile } from "../../Utils/utils";
import IntegrationCard from "./IntegrationCard";
import styles from "../../Styles/websiteIntegration.module.css";
import { WEBSITE_INTEGRATIONS } from "../../Utils/websiteIntegrations";

interface Integration {
  id: number;
  credentials: Record<string, any>;
  websites_integration: { type: string };
}

interface SelectedIntegration {
  integration?: Integration;
  locationId: string;
  integrationType: string;
  isNew?: boolean;
}

export default function WebsiteIntegration(props: any) {
  const { openEditModal, setOpenEditModal } = props;
  const cacheContext = useContext(CacheContext);
  const [integrations, setIntegrations] = useState<
    Record<string, Record<string, Integration>>
  >({});
  const [selectedIntegration, setSelectedIntegration] =
    useState<SelectedIntegration | null>(null);
  const [editedCredentials, setEditedCredentials] = useState<
    Record<string, any>
  >({});
  const [error, setError] = useState<string | null>(null);
  const [visiblePasswords, setVisiblePasswords] = useState<
    Record<string, boolean>
  >({});
  const [lockedFields, setLockedFields] = useState<Record<string, boolean>>({});

  const locations = getActiveLocations(cacheContext?.cache);

  /**
   * Normalize the integrations data into a more usable format.
   * The normalized format is a record where the keys are the location IDs,
   * and the values are records where the keys are the integration types,
   * and the values are the integration objects themselves.
   * @param {any} data - The data to be normalized
   * @return {Record<string, Record<string, Integration>>} - The normalized data
   */
  const normalizeIntegrations = (data: any) => {
    const normalized: Record<string, Record<string, Integration>> = {};
    Object.entries(data).forEach(
      ([locationId, locationIntegrations]: [string, any]) => {
        normalized[locationId] = {};
        locationIntegrations.forEach((integration: any) => {
          normalized[locationId][integration.websites_integration?.type] =
            integration;
        });
      }
    );
    return normalized;
  };

  /**
   * Fetches the integrations from the API
   */
  useEffect(() => {
    getData(urlEnum.websitesIntegration)
      .then((res) => {
        setIntegrations(normalizeIntegrations(res?.data || {}));
      })
      .catch((_err) => {
        setError(Vocabulary.integrationLoadingError);
      });
  }, []);

  useEffect(() => {
    if (!openEditModal) {
      setEditedCredentials({});
      setVisiblePasswords({});
      setLockedFields({});
    }
  }, [openEditModal]);

  /**
   * Handles edit click on an integration card. If the integration already exists, populate the edit modal with its credentials.
   * If the integration does not exist, search all locations for an integration of the same type to get the field structure of its credentials.
   * @param {string} locationId The id of the location
   * @param {string} integrationType The type of the integration
   */
  const handleEditClick = (locationId: string, integrationType: string) => {
    const integration = integrations[locationId]?.[integrationType];
    let credentials = integration?.credentials
      ? { ...integration.credentials }
      : {};

    // If adding new (no integration found), search all locations for this type to get field structure
    if (!integration) {
      for (const locId in integrations) {
        const existingIntegration = integrations[locId]?.[integrationType];
        if (existingIntegration?.credentials) {
          credentials = Object.keys(existingIntegration.credentials).reduce(
            (acc, key) => ({ ...acc, [key]: "" }),
            {}
          );
          break;
        }
      }
    }

    setSelectedIntegration({
      locationId,
      integrationType,
      integration,
      isNew: !integration,
    });
    setEditedCredentials(credentials);
    setLockedFields(
      integration
        ? Object.keys(credentials).reduce((acc, key) => ({ ...acc, [key]: true }), {})
        : {}
    );
    setOpenEditModal(true);
  };

  /**
   * Saves the integration data to the database.
   * If the integration already exists, it will be updated,
   * otherwise a new integration will be created.
   * The function will close the edit modal after saving and refresh the integrations list.
   */
  const saveIntegration = () => {
    if (!selectedIntegration) return;
    const url = selectedIntegration.integration?.id
      ? `${urlEnum.websitesIntegration}/update/${selectedIntegration.integration.id}`
      : `${urlEnum.websitesIntegration}/create`;

    postData(url, {
      credentials: editedCredentials,
      locatie_administrativa_id: selectedIntegration.locationId,
      type: selectedIntegration.integrationType,
    })
      .then((result: any) => {
        if (!result?.data) return;
        setOpenEditModal(false);
        getData(urlEnum.websitesIntegration).then((res) => {
          setIntegrations(normalizeIntegrations(res?.data || {}));
        });
      })
      .catch(() => setError(Vocabulary.credentialsUpdateFailed));
  };

  /**
   * Renders the component
   */
  return (
    <Box className={styles.container}>
      {error && (
        <Alert severity="error" style={{ marginBottom: "20px" }}>
          {error}
        </Alert>
      )}

      <div className={styles.gridContainer}>
        {locations.map((location) => (
          <IntegrationCard
            key={location.id}
            locationId={location.id.toString()}
            locationName={location.nume}
            integrations={integrations[location.id] || {}}
            onEdit={handleEditClick}
          />
        ))}
      </div>

      {openEditModal && selectedIntegration && (
        <GenericModal
          open={openEditModal}
          onClose={() => setOpenEditModal(false)}
          title={`${
            selectedIntegration.isNew ? Vocabulary.add : Vocabulary.edit
          } - ${
            WEBSITE_INTEGRATIONS.find(
              (w) => w.code === selectedIntegration.integrationType
            )?.name || selectedIntegration.integrationType
          }`}
          buttons={
            <Grid
              container
              spacing={isMobile() ? 1 : 3}
              style={{ padding: "16px" }}
            >
              <Grid item xs={12} sm={6}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => setOpenEditModal(false)}
                >
                  {Vocabulary.cancel}
                </Button>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={saveIntegration}
                  sx={{ color: "white" }}
                >
                  {Vocabulary.save}
                </Button>
              </Grid>
            </Grid>
          }
          children={
            <Grid
              container
              spacing={isMobile() ? 1 : 3}
              style={{ marginTop: "8px", marginBottom: "16px" }}
            >
              <Grid item xs={12}>
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#757575",
                    marginBottom: "16px",
                  }}
                >
                  {Vocabulary.integrationCredentials}
                </div>
              </Grid>
              {Object.entries(editedCredentials).map(([key, value]) => {
                const isPasswordField = key.toLowerCase().includes("password");
                const isPasswordVisible = visiblePasswords[key];
                const isLocked = !!lockedFields[key];
                const label =
                  key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, " ");

                return (
                  <Grid item xs={12} sm={6} key={key}>
                    <TextField
                      fullWidth
                      label={label}
                      value={value ?? ""}
                      disabled={isLocked}
                      type={
                        isPasswordField && !isPasswordVisible
                          ? "password"
                          : "text"
                      }
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        setEditedCredentials({
                          ...editedCredentials,
                          [key]: e.target.value,
                        })
                      }
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            {isLocked ? (
                              <Button
                                size="small"
                                onClick={() => {
                                  setLockedFields({
                                    ...lockedFields,
                                    [key]: false,
                                  });
                                  setEditedCredentials({
                                    ...editedCredentials,
                                    [key]: "",
                                  });
                                }}
                              >
                                {Vocabulary.changeValue}
                              </Button>
                            ) : isPasswordField ? (
                              <IconButton
                                onClick={() =>
                                  setVisiblePasswords({
                                    ...visiblePasswords,
                                    [key]: !isPasswordVisible,
                                  })
                                }
                                edge="end"
                              >
                                {isPasswordVisible ? (
                                  <VisibilityOff />
                                ) : (
                                  <Visibility />
                                )}
                              </IconButton>
                            ) : null}
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                );
              })}
            </Grid>
          }
        />
      )}
    </Box>
  );
}
